import {
  APP_INITIALIZER,
  type ApplicationConfig,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTransloco } from '@jsverse/transloco';

import { routes } from './app.routes';
import { Configuration } from './core/api/configuration';
import { BASE_PATH } from './core/api/variables';
import { environment } from '../environments/environment';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { initialDataLoaderInterceptor } from './core/interceptors/initial-data-loader.interceptor';
import { proactiveTokenRefreshInterceptor } from './core/interceptors/proactive-token-refresh.interceptor';
import { tokenRefreshInterceptor } from './core/interceptors/token-refresh.interceptor';
import { AuthService } from './core/services/auth.service';
import { AuthStorageService } from './core/services/auth-storage.service';
import { LanguageService } from './core/services/language.service';
import { TranslocoHttpLoaderService } from './core/services/transloco-loader.service';
import { catchError, type Observable, of } from 'rxjs';
import type { PersistedMerchantUser } from '@core/api/model/persistedMerchantUser';

function initializeAuth() {
  const authService = inject(AuthService);

  return (): Observable<PersistedMerchantUser | null> => {
    if (authService.isUserAuthenticated()) {
      return authService.initializeSession().pipe(catchError(() => of(null)));
    }
    return of(null);
  };
}

function initializeLanguage(): () => Promise<void> {
  const languageService = inject(LanguageService);
  return (): Promise<void> => languageService.initLanguage();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideHttpClient(
      withInterceptors([
        initialDataLoaderInterceptor,
        proactiveTokenRefreshInterceptor,
        authInterceptor,
        tokenRefreshInterceptor,
      ]),
    ),
    provideTransloco({
      config: {
        availableLangs: ['en', 'pl'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        fallbackLang: 'en',
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: !isDevMode(),
        },
      },
      loader: TranslocoHttpLoaderService,
    }),
    {
      provide: BASE_PATH,
      useValue: environment.api.proxyPath,
    },
    {
      provide: Configuration,
      useFactory: (): Configuration => {
        const authStorage = inject(AuthStorageService);
        return new Configuration({
          basePath: environment.api.proxyPath,
          credentials: {
            merchantPanelAuth: () => authStorage.getToken() ?? '',
            merchantPanelPasskeySetup: () => authStorage.getPasskeySetupToken() ?? '',
          },
        });
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      multi: true,
    },
  ],
};
